import type { TeamTag } from '@rhythmic/db/types'
import { generateUuid, TeamTagSchema } from '@rhythmic/types'
import type { Transaction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'

import { getTeamTagsCollection } from '../../getCollections'

const logger = useRhythmicLogger('useTeamContext:createTeamTag')

export interface CreateTeamTagPayload {
  id?: string
  tag: string
  color?: string | null
  type?: string | null
  context?: string | null
  integration_provider_id?: string | null
  external_id?: string | null
  workspace_id?: string | null
  team_id?: string
}

export interface CreateTeamTagContext {
  teamId: string
  workspaceId?: string | null
}

function validateParams(payload: CreateTeamTagPayload, context: CreateTeamTagContext) {
  const tag = payload.tag?.trim()
  if (!tag) throw new Error('Tag value is required')

  if (!context.teamId) throw new Error('Team context is not ready')

  return {
    tag,
    teamId: context.teamId,
    workspaceId: payload.workspace_id ?? context.workspaceId ?? null,
    color: payload.color ?? null,
    type: payload.type ?? null,
    context: payload.context ?? null,
    integration_provider_id: payload.integration_provider_id ?? null,
    external_id: payload.external_id ?? null,
  }
}

export default function handleCreateTeamTag(
  payload: CreateTeamTagPayload,
  context: CreateTeamTagContext,
): Transaction<TeamTag> {
  try {
    const resolved = validateParams(payload, context)
    const teamTagsCollection = getTeamTagsCollection(resolved.teamId)
    const now = new Date()

    const newTag = TeamTagSchema.parse({
      id: payload.id ?? generateUuid(),
      tag: resolved.tag,
      color: resolved.color,
      type: resolved.type,
      context: resolved.context,
      integration_provider_id: resolved.integration_provider_id,
      external_id: resolved.external_id,
      team_id: resolved.teamId,
      workspace_id: resolved.workspaceId,
      created_at: now,
      updated_at: now,
    })

    return teamTagsCollection.insert(newTag) as Transaction<TeamTag>
  } catch (error) {
    logger.error('createTeamTag failed', { error })
    if (error instanceof Error) throw error
    throw new Error('Failed to create team tag')
  }
}
